import { AuthApiS, AuthErrorS, AuthValidationS } from '@/features/Auth/outbound/share';
import type { ErrorJson } from '@/libs/errors';
import { isFeatureError } from '@/libs/errors/FeatureError';
import { T } from '@/libs/translation';
import { replaceBrToSpace } from '@/libs/util/replaceBrToSpace';
import { checkConsecutiveChar, isValidPassword } from '@/libs/validation/password';
import { Input, InputChangeEvent, Label } from '@/ui/component/atom';
import { ConfirmToggleActiveEvent, ModalBodyView } from '@/ui/component/cell';
import { ChangePasswordEvent } from '@/ui/component/event';
import { pipe } from '@fxts/core';
import { mauth } from '@marpple/auth';
import { isAxiosError } from 'axios';
import { html } from 'rune-ts';
import klass from '../Common/Modal.module.scss';
import { makeErrorPopup } from '../Popup/makeErrorPopup';

export class ChangePasswordModalBodyView extends ModalBodyView<{ email: string }> {
  private originalPasswordInput = new Input(
    { value: '', id: 'original_password' },
    {
      attr: {
        type: 'password',
        name: 'original_password',
        placeholder: T('mypage.change_password.placeholder.001'),
        required: true,
        autocomplete: 'off',
      },
    },
  );
  private passwordInput = new Input(
    { value: '', id: 'password' },
    {
      attr: {
        type: 'password',
        name: 'new_password',
        placeholder: T('mypage.change_password.placeholder.002'),
        required: true,
        autocomplete: 'new-password',
        maxlength: 30,
      },
      helper: {
        validators: [
          {
            message: T('mypage.set_password.validation.001'),
            validator: isValidPassword,
          },

          {
            message: T('mypage.set_password.validation.002'),
            validator: checkConsecutiveChar,
          },

          {
            message: T('mypage.set_password.validation.003'),
            validator: (value) => {
              return AuthValidationS.checkEmailChar(value, this.data.email ?? '');
            },
          },
        ],
      },
    },
  );

  private confirmPasswordInput = new Input(
    { value: '', id: 'new_password' },
    {
      attr: {
        type: 'password',
        name: 'confirm_password',
        placeholder: T('mypage.change_password.placeholder.003'),
        required: true,
        autocomplete: 'new-password',
        maxlength: 30,
      },
      helper: {
        type: 'check',
        validators: [
          {
            message: T('mypage.set_password.validation.004'),
            validator: (value: string) => {
              return this.passwordInput.data.value === value;
            },
          },
        ],
      },
    },
  );

  override async confirm() {
    try {
      if (this.passwordInput.data.value === this.confirmPasswordInput.data.value) {
        await pipe(
          {
            email: this.data.email,
            original_password: this.originalPasswordInput.data.value,
            password: this.passwordInput.data.value,
          },
          AuthValidationS.validationResetPwReqBody,
          AuthApiS.postChangePassword,
        );

        await mauth.refreshUser();
        this.dispatchEvent(ChangePasswordEvent, { bubbles: true });
        return true;
      } else {
        return false;
      }
    } catch (e) {
      if (isFeatureError(e)) {
        await makeErrorPopup(e, e.json);
        return false;
      }

      if (isAxiosError<ErrorJson>(e)) {
        if ([AuthErrorS.USER_009.name].includes(e.response?.data?.code ?? '')) {
          this.originalPasswordInput.changeValidatorOnce({
            message: replaceBrToSpace(e.response?.data?.message ?? T('common.errors.message')),
            validator: () => false,
          });
          return false;
        }

        await makeErrorPopup(e, e.response?.data);
        return false;
      }
      await makeErrorPopup(e);
      return false;
    }
  }

  override close() {
    return true;
  }

  protected override template() {
    return html`<div class="${klass.modal_body}">
      <div class="${klass.field}">
        ${new Label(
          {
            label: T('mypage.change_password.label.001'),
          },
          { required: true, for_id: 'original_password' },
        )}
        ${this.originalPasswordInput}
      </div>
      <div class="${klass.field}">
        ${new Label(
          {
            label: T('mypage.change_password.label.002'),
          },
          { required: true, for_id: 'password' },
        )}
        ${this.passwordInput}
      </div>
      <div class="${klass.field}">
        ${new Label(
          {
            label: T('mypage.change_password.label.003'),
          },
          { required: true, for_id: 'new_password' },
        )}
        ${this.confirmPasswordInput}
      </div>
    </div>`;
  }

  private enableConfirmButton() {
    return (
      this.originalPasswordInput.data.value !== '' &&
      this.passwordInput.data.value !== '' &&
      this.confirmPasswordInput.data.value !== '' &&
      this.passwordInput.data.value === this.confirmPasswordInput.data.value &&
      this.passwordInput.isValid() &&
      this.confirmPasswordInput.isValid()
    );
  }

  override onRender() {
    this.delegate(InputChangeEvent, Input, (e, input) => {
      if (input.option.attr?.name === 'new_password') {
        this.confirmPasswordInput.resetValidator();
      }
      this.dispatchEvent(ConfirmToggleActiveEvent, {
        bubbles: true,
        detail: {
          active: this.enableConfirmButton(),
        },
      });
    });

    this.delegate('input', Input, (e, view) => {
      this.dispatchEvent(ConfirmToggleActiveEvent, {
        bubbles: true,
        detail: {
          active: this.enableConfirmButton(),
        },
      });
    });
  }
}
